import { useState, useEffect } from "react";
import { Activity, RefreshCw, Database, HardDrive, Cpu, MemoryStick, Archive, Phone } from "lucide-react";
import api from "@/lib/api";
import { Section } from "./Section";

const fmtBytes = (n) => {
  if (n === null || n === undefined) return "—";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0, v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${u[i]}`;
};

const fmtUptime = (s) => {
  if (s === null || s === undefined) return "—";
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  return d > 0 ? `${d}d ${h}h ${m}m` : h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const Bar = ({ percent, danger }) => (
  <div className="h-1.5 bg-slate-800 rounded overflow-hidden mt-1">
    <div className={`h-full ${percent >= 90 || danger ? "bg-red-500" : percent >= 75 ? "bg-amber-500" : "bg-emerald-500"}`}
      style={{ width: `${Math.min(100, percent || 0)}%` }} />
  </div>
);

const Card = ({ icon: Icon, color, title, children, testid }) => (
  <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-3" data-testid={testid}>
    <div className="flex items-center gap-2 mb-1.5">
      <Icon size={14} className={color} />
      <span className="text-xs font-semibold text-slate-300">{title}</span>
    </div>
    {children}
  </div>
);

export function SystemStatus({ t, open, onToggle }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setBusy(true); setErr("");
    try { const { data } = await api.get("/system/status"); setData(data); }
    catch { setErr(t("Nie udało się pobrać stanu systemu")); }
    finally { setBusy(false); }
  };

  useEffect(() => { if (open && !data) load(); /* eslint-disable-next-line */ }, [open]);

  const db = data?.database, disk = data?.disk, mem = data?.memory, cpu = data?.cpu, gw = data?.gateway, lb = data?.last_backup;

  return (
    <Section icon={Activity} iconColor="text-emerald-400" title={t("Stan systemu")}
      open={open} onToggle={onToggle} testid="settings-sec-sysstatus">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] text-slate-400">{t("Podgląd kondycji serwera bez logowania przez SSH.")}</p>
        <button type="button" onClick={load} disabled={busy} data-testid="sysstatus-refresh"
          className="inline-flex items-center gap-1.5 text-xs bg-slate-700 hover:bg-slate-600 rounded px-2.5 py-1.5 disabled:opacity-50">
          <RefreshCw size={13} className={busy ? "animate-spin" : ""} /> {t("Odśwież")}
        </button>
      </div>
      {err && <div className="text-[11px] text-red-400 mb-2" data-testid="sysstatus-error">{err}</div>}
      {!data && !err && <div className="text-[11px] text-slate-500">{t("Ładowanie…")}</div>}
      {data && (
        <div className="grid grid-cols-2 gap-2" data-testid="sysstatus-grid">
          <Card icon={Activity} color="text-cyan-400" title={t("Backend")} testid="sysstatus-backend">
            <div className="text-[11px] text-slate-400">{t("Działa od")}: <span className="text-slate-200">{fmtUptime(data.backend?.uptime_seconds)}</span></div>
            <div className="text-[11px] text-slate-400">{t("Wersja")}: <span className="text-slate-200">{data.backend?.version || "—"}</span></div>
          </Card>
          <Card icon={Database} color="text-indigo-400" title={t("Baza MongoDB")} testid="sysstatus-db">
            <div className={`text-[11px] ${db?.connected ? "text-emerald-400" : "text-red-400"}`}>
              {db?.connected ? t("Połączona") : t("Brak połączenia")}
            </div>
            {db?.connected && <div className="text-[11px] text-slate-400">{t("Dane")}: <span className="text-slate-200">{fmtBytes(db.data_size)}</span> · {db.collections} {t("kolekcji")}</div>}
          </Card>
          <Card icon={HardDrive} color="text-amber-400" title={t("Dysk")} testid="sysstatus-disk">
            {disk?.error ? <div className="text-[11px] text-slate-500">{t("Brak danych")}</div> : (
              <>
                <div className="text-[11px] text-slate-400">{fmtBytes(disk?.free)} {t("wolne")} / {fmtBytes(disk?.total)}</div>
                <Bar percent={disk?.percent} />
                <div className="text-[10px] text-slate-500 mt-0.5">{disk?.percent}% {t("zajęte")}</div>
              </>
            )}
          </Card>
          <Card icon={Archive} color="text-sky-400" title={t("Ostatni backup")} testid="sysstatus-backup">
            {lb ? (
              <>
                <div className="text-[11px] text-slate-200 truncate">{lb.created?.slice(0, 16).replace("T", " ")}</div>
                <div className="text-[10px] text-slate-500">{fmtBytes(lb.size)} {lb.encrypted ? "· 🔒" : ""}</div>
              </>
            ) : <div className="text-[11px] text-slate-500">{t("Brak kopii")}</div>}
          </Card>
          <Card icon={Cpu} color="text-rose-400" title={t("Procesor")} testid="sysstatus-cpu">
            {cpu?.error ? <div className="text-[11px] text-slate-500">{t("Brak danych")}</div> : (
              <>
                <div className="text-[11px] text-slate-400">{cpu?.percent}% · {cpu?.cores} {t("rdzeni")}</div>
                <Bar percent={cpu?.percent} />
              </>
            )}
          </Card>
          <Card icon={MemoryStick} color="text-fuchsia-400" title={t("Pamięć RAM")} testid="sysstatus-mem">
            {mem?.error ? <div className="text-[11px] text-slate-500">{t("Brak danych")}</div> : (
              <>
                <div className="text-[11px] text-slate-400">{fmtBytes(mem?.used)} / {fmtBytes(mem?.total)}</div>
                <Bar percent={mem?.percent} />
              </>
            )}
          </Card>
          <Card icon={Phone} color="text-teal-400" title={t("Bramka VoIP")} testid="sysstatus-gateway">
            <div className="text-[11px] text-slate-400">Asterisk: <span className={gw?.asterisk === "running" ? "text-emerald-400" : "text-slate-300"}>{gw?.asterisk || "—"}</span></div>
            <div className="text-[11px] text-slate-400">TURN: <span className={gw?.turn === "running" ? "text-emerald-400" : "text-slate-300"}>{gw?.turn || "—"}</span></div>
          </Card>
        </div>
      )}
    </Section>
  );
}
